import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-hot-toast';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { routes } from 'constants/routes';
import { updateBusiness } from 'constants/apiUrls';
import { http } from 'middleware/apiMiddleware';
import { useLazyCountryListQuery } from 'store/apis/countryListApi';
import { useLazyStateListQuery } from 'store/apis/stateListApi';
import { useLazyCityListQuery } from 'store/apis/cityListApi';
import { useLazyCommunityListQuery } from 'store/apis/communityListApi';
import {
  useSubmitOnboardingRequestMutation,
  OnboardingRequestPayload,
} from 'store/apis/onboardingApi';
import { useCategoriesQuery } from 'store/apis/categoryApi';
import { useExternalLinkTypesQuery } from 'store/apis/externalLinksApi';
import {
  extractAndStoreReferralCode,
  getStoredReferralCode,
  clearStoredReferralCode,
} from 'utils/common';

import BasicDetailsForm from 'components/OnboardingBusiness/BasicDetailsForm';
import AdditionalInformationForm from 'components/OnboardingBusiness/AdditionalInformationForm';
import BusinessPhotosUpload from 'components/OnboardingBusiness/BusinessPhotosUpload';
import ProgressIndicator from 'components/OnboardingBusiness/ProgressIndicator';
import NavigationButtons from 'components/OnboardingBusiness/NavigationButtons';

import {
  IOnboardingBusinessProps,
  SelectOption,
  BusinessEditPhotoProps,
  ImageStatus,
  PhotoSource,
} from './types';

const OnboardingBusiness: React.FC<IOnboardingBusinessProps> = () => {
  const navigate = useNavigate();
  // State management for onboarding flow
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;
  const [createdBusinessUuid, setCreatedBusinessUuid] = useState<string>('');
  const [createdBusinessData, setCreatedBusinessData] = useState<Record<string, unknown> | null>(
    null,
  );
  const [uploadedFiles, setUploadedFiles] = useState<BusinessEditPhotoProps[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileStatuses, setFileStatuses] = useState<{
    [key: number]: 'uploading' | 'success' | 'failed';
  }>({});

  const [fetchCountries, { data: countries, isLoading: isLoadingCountries }] =
    useLazyCountryListQuery();
  const [fetchStates, { data: states, isLoading: isLoadingStates }] = useLazyStateListQuery();
  const [fetchCities, { data: cities, isLoading: isLoadingCities }] = useLazyCityListQuery();
  const [fetchCommunities, { data: communities, isLoading: isLoadingCommunities }] =
    useLazyCommunityListQuery();
  const [submitOnboardingRequest, { isLoading: isSubmitting }] =
    useSubmitOnboardingRequestMutation();
  const [isSubmittingPhotos, setIsSubmittingPhotos] = useState(false);

  // Fetch categories from API
  const { data: categoriesData, isLoading: isLoadingCategories } = useCategoriesQuery();

  // Fetch external link types from API
  const { data: externalLinkTypes, isLoading: isLoadingExternalLinks } =
    useExternalLinkTypesQuery();

  // More strict URL validation - requires proper domain with valid TLD (no @ in domain to reject emails)
  const URL_VALIDATION_REGEX =
    /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9%._+~#=]{1,256}\.[a-zA-Z0-9()]{2,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;

  const validationSchema = Yup.object().shape({
    businessName: Yup.string()
      .trim()
      .max(255, 'Business Name can be of max 255 characters.')
      .required('This field cannot be empty')
      .test('check_empty_spaces', 'Please enter a valid name', (val: string | undefined) => {
        return !!val && val.trim().length > 2;
      }),
    streetAddress: Yup.string().trim().max(1024, 'Street Address can be of max 1024 characters'),
    country: Yup.object()
      .shape({
        name: Yup.string().required('This field cannot be empty'),
        value: Yup.string().required('This field cannot be empty'),
      })
      .required('This field cannot be empty'),
    state: Yup.object()
      .shape({
        name: Yup.string().required('This field cannot be empty'),
        value: Yup.string().required('This field cannot be empty'),
      })
      .required('This field cannot be empty'),
    city: Yup.object()
      .shape({
        name: Yup.string().required('This field cannot be empty'),
        value: Yup.string().required('This field cannot be empty'),
      })
      .required('This field cannot be empty'),
    zipcode: Yup.string()
      .trim()
      .required('This field cannot be left blank')
      .test('valid-zipcode', function (value) {
        if (!value) return false;

        const country = this.parent.country;
        const countryCode = country?.value?.toUpperCase();

        // Country-specific zipcode validation
        switch (countryCode) {
          case 'US': // United States - 5 digits or 5+4 format
            if (!/^\d{5}(-\d{4})?$/.test(value)) {
              return this.createError({
                message: 'US zipcode must be 5 digits (e.g., 12345) or 9 digits (e.g., 12345-6789)',
              });
            }
            break;

          case 'CA': // Canada - A1A 1A1 format
            if (!/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(value)) {
              return this.createError({
                message: 'Canadian postal code must be in format A1A 1A1',
              });
            }
            break;

          case 'GB': // United Kingdom - Various formats
          case 'UK':
            if (!/^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i.test(value)) {
              return this.createError({
                message: 'UK postcode must be in valid format (e.g., SW1A 1AA)',
              });
            }
            break;

          case 'IN': // India - 6 digits
            if (!/^\d{6}$/.test(value)) {
              return this.createError({
                message: 'Indian PIN code must be 6 digits',
              });
            }
            break;

          case 'AU': // Australia - 4 digits
            if (!/^\d{4}$/.test(value)) {
              return this.createError({
                message: 'Australian postcode must be 4 digits',
              });
            }
            break;

          case 'MX': // Mexico - 5 digits
            if (!/^\d{5}$/.test(value)) {
              return this.createError({
                message: 'Mexican postal code must be 5 digits',
              });
            }
            break;

          case 'BR': // Brazil - 8 digits (XXXXX-XXX)
            if (!/^\d{5}-?\d{3}$/.test(value)) {
              return this.createError({
                message: 'Brazilian CEP must be 8 digits (e.g., 12345-678)',
              });
            }
            break;

          case 'DE': // Germany - 5 digits
          case 'FR': // France - 5 digits
          case 'ES': // Spain - 5 digits
          case 'IT': // Italy - 5 digits
            if (!/^\d{5}$/.test(value)) {
              return this.createError({
                message: 'Postal code must be 5 digits',
              });
            }
            break;

          case 'NL': // Netherlands - 4 digits + 2 letters
            if (!/^\d{4}\s?[A-Z]{2}$/i.test(value)) {
              return this.createError({
                message: 'Dutch postcode must be 4 digits followed by 2 letters (e.g., 1234 AB)',
              });
            }
            break;

          case 'JP': // Japan - 7 digits (XXX-XXXX)
            if (!/^\d{3}-?\d{4}$/.test(value)) {
              return this.createError({
                message: 'Japanese postal code must be 7 digits (e.g., 123-4567)',
              });
            }
            break;

          case 'CN': // China - 6 digits
            if (!/^\d{6}$/.test(value)) {
              return this.createError({
                message: 'Chinese postal code must be 6 digits',
              });
            }
            break;

          default:
            // Generic validation for other countries (3-10 alphanumeric characters)
            if (!/^[A-Za-z0-9\s-]{3,10}$/.test(value)) {
              return this.createError({
                message: 'Zipcode must be 3-10 characters',
              });
            }
        }

        return true;
      }),
    referralCode: Yup.string()
      .trim()
      .max(50, 'Referral code can be of max 50 characters')
      .test('no-empty-spaces', 'Referral code cannot be only spaces', (val: string | undefined) => {
        if (!val || val === '') return true; // Allow empty (optional field)
        return val.trim().length > 0; // If provided, must not be only spaces
      }),
    category: Yup.object()
      .shape({
        name: Yup.string(),
        value: Yup.string(),
      })
      .nullable(),
    website: Yup.string()
      .trim()
      .max(1024, 'Website can be of max 1024 characters')
      .test('is-valid-url', 'Please enter a valid website URL', (val: string | undefined) => {
        if (!val || val.trim() === '') return true; // Allow empty
        return URL_VALIDATION_REGEX.test(val);
      }),
    findUsAt: Yup.string()
      .trim()
      .max(1024, 'URL can be of max 1024 characters')
      .test('is-valid-url', 'Please enter a valid URL', (val: string | undefined) => {
        if (!val || val.trim() === '') return true; // Allow empty
        return URL_VALIDATION_REGEX.test(val);
      }),
    businessHoursNote: Yup.string()
      .trim()
      .max(1024, 'Notes can be of max 1024 characters')
      .test(
        'no-empty-spaces',
        'Business notes cannot be only spaces',
        (val: string | undefined) => {
          if (!val || val === '') return true; // Allow empty (optional field)
          return val.trim().length > 0; // If provided, must not be only spaces
        },
      ),
    businessPhones: Yup.array().of(
      Yup.string()
        .trim()
        .max(50, 'Phone number can be of max 50 characters')
        .test('is-valid-phone', 'Please enter a valid phone number', (val: string | undefined) => {
          if (!val || val.trim() === '') return true; // Allow empty

          try {
            // Try to parse and validate the phone number
            const phoneNumber = parsePhoneNumber(val.trim());
            if (phoneNumber) {
              return phoneNumber.isValid();
            }

            // If can't parse with country code, try basic validation
            const isValid = isValidPhoneNumber(val.trim());
            return isValid;
          } catch (error) {
            // Basic fallback validation (10-15 digits with optional +)
            const cleaned = val.trim().replace(/[\s\-()]/g, '');
            if (cleaned.length < 10) {
              return false; // Minimum 10 digits
            }
            if (cleaned.length > 15) {
              return false; // Maximum 15 digits
            }
            // Must contain only digits and optional + at start
            return /^\+?\d{10,15}$/.test(cleaned);
          }
        }),
    ),
    socialLinks: Yup.array().of(
      Yup.object().shape({
        platform: Yup.string().trim(),
        url: Yup.string()
          .trim()
          .test('is-valid-url-or-handle', function (val: string | undefined) {
            if (!val || val.trim() === '') return true; // Allow empty

            const platform = this.parent.platform;
            const trimmedVal = val.trim();

            // Instagram and X (Twitter) accept handles/usernames
            if (platform === 'instagram' || platform === 'twitter') {
              const handleRegex = /^@?[a-zA-Z0-9._]+$/;
              if (handleRegex.test(trimmedVal)) {
                return true; // Valid handle/username
              }
              // Also accept full URLs for these platforms
              if (URL_VALIDATION_REGEX.test(trimmedVal)) {
                return true;
              }
              return this.createError({
                message: 'Please enter a valid username',
              });
            }

            // Facebook, LinkedIn, and Others only accept full URLs
            if (!URL_VALIDATION_REGEX.test(trimmedVal)) {
              return this.createError({
                message: `Please enter a valid ${platform === 'facebook' ? 'Facebook' : platform === 'linkedin' ? 'LinkedIn' : ''} URL`,
              });
            }
            return true;
          }),
      }),
    ),
    businessHours: Yup.mixed().test(
      'business-hours',
      'Please fill in all business hours time fields',
      function (val: Record<string, unknown> | undefined) {
        const { businessHoursTimes } = this.parent;
        const v = val || {};

        // Check if any day is enabled
        const enabledDays = Object.keys(v).filter((day) => v[day]);

        // No days enabled, validation passes (business hours are optional)
        if (enabledDays.length === 0) return true;

        // Check each enabled day has valid time slots
        for (const day of enabledDays) {
          const dayTimes = businessHoursTimes[day] || [];
          // Skip validation if no time slots exist for this day
          if (dayTimes.length === 0) continue;

          for (const slot of dayTimes) {
            // Only fail if the user has started filling times but left them incomplete
            if ((slot.startTime && !slot.endTime) || (!slot.startTime && slot.endTime)) {
              return false; // One time is filled, the other is not
            }
          }
        }

        return true;
      },
    ),
  });

  const formik = useFormik({
    initialValues: {
      community: null as SelectOption | null,
      businessName: '',
      streetAddress: '',
      country: null as SelectOption | null,
      state: null as SelectOption | null,
      city: null as SelectOption | null,
      zipcode: '',
      referralCode: '',
      category: null as SelectOption | null,
      website: '',
      findUsAt: '',
      businessHoursNote: '',
      businessPhones: [''] as string[],
      socialLinks: [{ platform: '', url: '' }] as {
        platform: string;
        url: string;
      }[],
      businessHours: {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
      },
      businessHoursTimes: {
        monday: [{ startTime: '', endTime: '' }],
        tuesday: [{ startTime: '', endTime: '' }],
        wednesday: [{ startTime: '', endTime: '' }],
        thursday: [{ startTime: '', endTime: '' }],
        friday: [{ startTime: '', endTime: '' }],
        saturday: [{ startTime: '', endTime: '' }],
        sunday: [{ startTime: '', endTime: '' }],
      },
    },
    validationSchema,
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async () => {
      // This is called when form is submitted, but in this flow we handle navigation in handleNext
      navigate(routes.home, {
        state: {
          fromOnboarding: true,
          businessUuid: createdBusinessUuid,
          businessData: createdBusinessData,
        },
      });
    },
  });

  const { values, setFieldValue, setFieldTouched, errors, touched } = formik;

  const isStep1Complete = useMemo(() => {
    return !!(
      values.businessName &&
      values.country &&
      values.state &&
      values.city &&
      values.zipcode &&
      values.zipcode.length >= 3
    );
  }, [values.businessName, values.country, values.state, values.city, values.zipcode]);

  const countryOptions = useMemo((): SelectOption[] => {
    if (!countries?.result?.length) return [];

    const options = countries.result.map(({ name, isoCode }) => ({
      name: name,
      value: isoCode,
    }));

    // Sort to ensure US comes first
    return options.sort((a, b) => {
      if (a.value === 'US') return -1;
      if (b.value === 'US') return 1;
      return a.name.localeCompare(b.name);
    });
  }, [countries]);

  const stateOptions = useMemo((): SelectOption[] => {
    return states?.result?.length
      ? states.result.map(({ name, isoCode }) => ({
          name: name,
          value: isoCode,
        }))
      : [];
  }, [states]);

  const cityOptions = useMemo((): SelectOption[] => {
    return cities?.result?.length
      ? cities.result.map(({ name }) => ({
          name: name,
          value: name,
        }))
      : [];
  }, [cities]);

  const communityOptions = useMemo((): SelectOption[] => {
    // API now returns an array directly with full data including address
    if (Array.isArray(communities) && communities.length > 0) {
      return communities.map((comm: any) => ({
        name: comm.name,
        value: comm.uuid,
        address: comm.address, // Store the full address data
      }));
    }

    return [];
  }, [communities]);

  const categoryOptions = useMemo((): SelectOption[] => {
    if (!categoriesData || categoriesData.length === 0) {
      return [];
    }
    return categoriesData.map((category) => ({
      name: category.name,
      value: category.uuid, // Use UUID as value!
    }));
  }, [categoriesData]);

  useEffect(() => {
    fetchCountries();
    // Try to fetch communities, but don't break if it fails
    fetchCommunities().catch(() => {
      // Community API failed (optional field)
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Extract and prefill referral code from URL on mount
  useEffect(() => {
    // Extract from URL first (stores in session storage)
    extractAndStoreReferralCode();

    // Get stored referral code (either just extracted or previously stored)
    const storedReferralCode = getStoredReferralCode();

    // Prefill the referral code field if we have one
    if (storedReferralCode && !values.referralCode) {
      setFieldValue('referralCode', storedReferralCode);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set default country to US when countries load
  useEffect(() => {
    if (countryOptions.length > 0 && !values.country) {
      const usCountry = countryOptions.find((c) => c.value === 'US');
      if (usCountry) {
        // Save scroll position before state update
        const scrollY = window.scrollY;
        setFieldValue('country', usCountry);
        // Restore scroll position after state update
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryOptions]);

  useEffect(() => {
    if (values.country?.value) {
      // Save scroll position before state update
      const scrollY = window.scrollY;
      fetchStates({ countryCode: values.country.value });
      setFieldValue('state', null);
      setFieldValue('city', null);
      // Restore scroll position after state update
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.country]);

  useEffect(() => {
    if (values.country?.value && values.state?.value) {
      // Save scroll position before state update
      const scrollY = window.scrollY;
      fetchCities({
        countryCode: values.country.value,
        stateCode: values.state.value,
      });
      setFieldValue('city', null);
      // Restore scroll position after state update
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.state]);

  const handleNext = async () => {
    if (currentStep === 1) {
      const step1Fields: Array<keyof typeof values> = [
        'businessName',
        'country',
        'state',
        'city',
        'zipcode',
      ];
      const errors = await formik.validateForm();
      const step1Errors = step1Fields.filter((field) => errors[field]);

      if (step1Errors.length > 0) {
        step1Fields.forEach((field) => formik.setFieldTouched(field, true));
        // Inline errors will show automatically, no alert needed
        return;
      }

      setCurrentStep(currentStep + 1);
      return;
    }

    if (currentStep === 2) {
      // Validate that Step 1 fields still exist (safety check)
      if (
        !values.businessName ||
        !values.country ||
        !values.state ||
        !values.city ||
        !values.zipcode
      ) {
        toast.error('Please complete Basic Details (Step 1) before proceeding');
        setCurrentStep(1);
        return;
      }

      // Run Yup validation
      const errors = await formik.validateForm();

      // Check if there are any validation errors in Step 2 fields
      if (
        errors.website ||
        errors.findUsAt ||
        errors.businessHoursNote ||
        errors.businessPhones ||
        errors.socialLinks ||
        errors.businessHours
      ) {
        // Mark all step 2 fields as touched to show validation errors
        formik.setFieldTouched('website', true);
        formik.setFieldTouched('findUsAt', true);
        formik.setFieldTouched('businessHoursNote', true);
        formik.setFieldTouched('businessPhones', true);
        formik.setFieldTouched('socialLinks', true);
        formik.setFieldTouched('businessHours', true);

        // Show toast with validation error
        if (errors.businessHours) {
          toast.error(errors.businessHours as string);
        } else if (errors.businessPhones) {
          toast.error('Please enter valid phone numbers');
        } else if (errors.socialLinks) {
          toast.error('Please enter valid URLs for all social media links');
        } else {
          toast.error('Please fix the validation errors before proceeding');
        }
        return;
      }

      try {
        const dayNameToNumber: { [key: string]: number } = {
          monday: 1,
          tuesday: 2,
          wednesday: 3,
          thursday: 4,
          friday: 5,
          saturday: 6,
          sunday: 7,
        };

        const businessHoursData = Object.keys(values.businessHours)
          .filter((day) => values.businessHours[day as keyof typeof values.businessHours] === true)
          .flatMap((day) => {
            const dayTimes =
              values.businessHoursTimes[day as keyof typeof values.businessHoursTimes] || [];
            return dayTimes
              .filter(
                (slot: { startTime: string; endTime: string }) => slot.startTime && slot.endTime,
              )
              .map((slot: { startTime: string; endTime: string }) => ({
                day: dayNameToNumber[day],
                startTime: slot.startTime,
                endTime: slot.endTime,
              }));
          });

        // Helper function to convert handles to full URLs
        const convertHandleToUrl = (platform: string, urlOrHandle: string): string => {
          const trimmedValue = urlOrHandle.trim();

          // If it's already a full URL, return as-is
          if (URL_VALIDATION_REGEX.test(trimmedValue)) {
            return trimmedValue;
          }

          // Remove @ symbol if present
          const handle = trimmedValue.replace(/^@/, '');

          // Convert handle to full URL based on platform
          switch (platform.toLowerCase()) {
            case 'instagram':
              return `https://www.instagram.com/${handle}/`;
            case 'twitter':
              return `https://twitter.com/${handle}`;
            default:
              return trimmedValue; // Return as-is for other platforms
          }
        };

        // Map social links to the correct format with UUIDs
        const openExternalLinks = values.socialLinks
          .filter((link) => link && link.platform && link.url && link.url.trim())
          .map((link) => {
            // Find the matching external link type from the API
            const externalLinkType = externalLinkTypes?.find(
              (type) => type.name.toLowerCase() === link.platform.toLowerCase(),
            );

            // Convert handle to full URL if needed
            const fullUrl = convertHandleToUrl(link.platform, link.url);

            return {
              metaExternalLinkUuid: externalLinkType?.metaExternalLinkUuid || '',
              url: fullUrl,
            };
          })
          .filter((link) => link.metaExternalLinkUuid); // Only include links with valid UUIDs

        const payload: OnboardingRequestPayload = {
          business: {
            name: values.businessName?.trim() || '',
            address: {
              address1: values.streetAddress?.trim() || '',
              address2: '',
              zipCode: values.zipcode?.trim() || '',
              state: values.state?.name || '',
              stateShortCode: values.state?.value || '',
              country: values.country?.name || '',
              countryShortCode: values.country?.value || '',
              city: values.city?.name || '',
            },
            logo: null,
            website: values.website?.trim() || '',
            phoneNumber: values.businessPhones
              .filter((phone) => phone.trim())
              .map((phone) => phone.trim()),
            // Send category UUID (value is the UUID from the API)
            categories: values.category?.value ? [values.category.value] : [],
            // Send external links with proper UUID mapping
            openExternalLinks,
            businessOpeningHours: businessHoursData,
            businessHourNotes: values.businessHoursNote?.trim() || '',
          },
          // Add optional community UUID if selected
          communityUuid: values.community?.value || undefined,
          // Add optional referral code if provided
          referralCode: values.referralCode?.trim() || undefined,
        };

        const response = await submitOnboardingRequest(payload).unwrap();

        // Clear referral code from session storage after successful submission
        clearStoredReferralCode();

        // Store the created business UUID and data for later use
        if (response?.business?.uuid) {
          setCreatedBusinessUuid(response.business.uuid);
          setCreatedBusinessData(response.business);
        }

        setCurrentStep(currentStep + 1);
      } catch (error: any) {
        let errorMessage = '';

        if (error?.data?.message) {
          errorMessage = error.data.message;
        } else if (error?.data?.error) {
          errorMessage = error.data.error;
        } else if (error?.data?.detail) {
          errorMessage = error.data.detail;
        } else if (typeof error?.data === 'object' && error?.data) {
          errorMessage = Object.entries(error.data)
            .map(([field, messages]) => {
              if (Array.isArray(messages)) {
                return `${field}: ${messages.join(', ')}`;
              }
              return `${field}: ${JSON.stringify(messages)}`;
            })
            .join('\n');
        } else if (typeof error?.data === 'string') {
          errorMessage = error.data;
        } else {
          errorMessage = `API Error: ${error?.status || 'Unknown'} - No error details provided`;
        }

        toast.error(`Error submitting onboarding request: ${errorMessage}`);
      }
      return;
    }

    if (currentStep === 3) {
      // Collect UUIDs of successfully uploaded photos
      const uploadedPhotoUuids = uploadedFiles
        .filter(
          (file) =>
            file.status === ImageStatus.UPLOADED && file.uuid && !file.uuid.startsWith('temp-'),
        )
        .map((file) => file.uuid);

      // If there are photos to save, make PATCH request to update business
      try {
        if (uploadedPhotoUuids.length > 0 && createdBusinessUuid) {
          setIsSubmittingPhotos(true);
          const { error, data } = await http.patch(updateBusiness(createdBusinessUuid), {
            photosData: {
              uploadedPhotos: uploadedPhotoUuids,
              deletedPhotos: [],
            },
          });

          if (!error && !!data) {
            toast.success('Business photos saved successfully!');
          } else {
            toast.error('Failed to save business photos. Please try again.');
          }
        }
      } catch (error) {
        toast.error('Failed to save business photos. Please try again.');
      } finally {
        setIsSubmittingPhotos(false);
      }

      // Navigate to home with the created business data
      navigate(routes.home, {
        state: {
          fromOnboarding: true,
          businessUuid: createdBusinessUuid,
          businessData: createdBusinessData,
        },
      });
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    // Skip photos and go directly to home with the created business data
    navigate(routes.home, {
      state: {
        fromOnboarding: true,
        businessUuid: createdBusinessUuid,
        businessData: createdBusinessData,
      },
    });
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files);
    const imageFiles = fileArray.filter((file) => file.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      toast.error('Please select only image files.');
      return;
    }

    if (uploadedFiles.length + imageFiles.length > 5) {
      toast.error('You can upload maximum 5 photos.');
      return;
    }

    const newImageFiles: BusinessEditPhotoProps[] = imageFiles.map((file) => ({
      rawImage: file,
      photoUrl: '',
      status: ImageStatus.QUEUED,
      uuid: `temp-${Date.now()}-${Math.random()}`,
      source: PhotoSource.BUSINESS,
    }));

    setUploadedFiles((prev) => [...prev, ...newImageFiles]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
  };

  const removeFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    setFileStatuses((prev) => {
      const newStatuses = { ...prev };
      delete newStatuses[index];
      const reindexedStatuses: {
        [key: number]: 'uploading' | 'success' | 'failed';
      } = {};
      Object.keys(newStatuses).forEach((key) => {
        const oldIndex = parseInt(key);
        if (oldIndex > index) {
          reindexedStatuses[oldIndex - 1] = newStatuses[oldIndex];
        } else if (oldIndex < index) {
          reindexedStatuses[oldIndex] = newStatuses[oldIndex];
        }
      });
      return reindexedStatuses;
    });
  };

  const retryUpload = (index: number) => {
    setFileStatuses((prev) => ({
      ...prev,
      [index]: 'uploading',
    }));

    setTimeout(() => {
      setFileStatuses((prev) => ({
        ...prev,
        [index]: 'success',
      }));
    }, 2000);
  };

  const previewFile = (file: File | BusinessEditPhotoProps) => {
    let url: string;

    if (file instanceof File) {
      url = URL.createObjectURL(file);
    } else {
      if (file.photoUrl) {
        url = file.photoUrl;
      } else {
        url = URL.createObjectURL(file.rawImage as File);
      }
    }

    window.open(url, '_blank');
  };

  const toggleBusinessHour = (day: string) => {
    // Save current scroll position to prevent auto-scroll
    const scrollY = window.scrollY;

    const isCurrentlyEnabled = values.businessHours[day as keyof typeof values.businessHours];

    // If toggling ON (enabling the day), copy hours from previous day
    if (!isCurrentlyEnabled) {
      const dayOrder = [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
      ];
      const currentDayIndex = dayOrder.indexOf(day);

      // Find the previous day with hours set
      let previousDayTimes = [{ startTime: '', endTime: '' }];
      for (let i = currentDayIndex - 1; i >= 0; i--) {
        const prevDay = dayOrder[i];
        if (values.businessHours[prevDay as keyof typeof values.businessHours]) {
          const prevTimes =
            values.businessHoursTimes[prevDay as keyof typeof values.businessHoursTimes];
          if (prevTimes && prevTimes.length > 0 && prevTimes[0].startTime) {
            previousDayTimes = JSON.parse(JSON.stringify(prevTimes)); // Deep copy
            break;
          }
        }
      }

      // Set the hours for this day
      setFieldValue('businessHoursTimes', {
        ...values.businessHoursTimes,
        [day]: previousDayTimes,
      });
    }

    // Toggle the day on/off
    setFieldValue('businessHours', {
      ...values.businessHours,
      [day]: !isCurrentlyEnabled,
    });

    // Restore scroll position after state update
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });
  };

  // Handler for community selection - auto-fill Country, State, and City only
  const handleCommunitySelect = async (selectedCommunity: SelectOption) => {
    if (selectedCommunity && selectedCommunity.address) {
      const address = selectedCommunity.address;
      // Save scroll position to prevent auto-scroll during auto-fill
      const scrollY = window.scrollY;

      // Auto-fill country
      if (address.country && address.countryShortCode) {
        const countryOption = {
          name: address.country,
          value: address.countryShortCode,
        };
        setFieldValue('country', countryOption);

        // Fetch states for the selected country
        if (address.countryShortCode) {
          await fetchStates({ countryCode: address.countryShortCode });
        }
      }

      // Auto-fill state after country is set
      if (address.state && address.stateShortCode && address.countryShortCode) {
        // Wait a bit for states to be fetched
        setTimeout(async () => {
          const stateOption = {
            name: address.state!,
            value: address.stateShortCode!,
          };
          setFieldValue('state', stateOption);

          // Fetch cities for the selected state
          if (address.countryShortCode && address.stateShortCode) {
            await fetchCities({
              countryCode: address.countryShortCode,
              stateCode: address.stateShortCode,
            });
          }

          // Auto-fill city after state is set
          if (address.city) {
            setTimeout(() => {
              const cityOption = {
                name: address.city!,
                value: address.city!,
              };
              setFieldValue('city', cityOption);
              // Restore scroll position after all auto-fill is done
              requestAnimationFrame(() => {
                window.scrollTo(0, scrollY);
              });
            }, 300);
          } else {
            // Restore scroll position if no city to auto-fill
            requestAnimationFrame(() => {
              window.scrollTo(0, scrollY);
            });
          }
        }, 300);
      } else {
        // Restore scroll position if no state to auto-fill
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
        });
      }
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicDetailsForm
            values={values}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            errors={errors}
            touched={touched}
            communityOptions={communityOptions}
            countryOptions={countryOptions}
            stateOptions={stateOptions}
            cityOptions={cityOptions}
            isLoadingCommunities={isLoadingCommunities}
            isLoadingCountries={isLoadingCountries}
            isLoadingStates={isLoadingStates}
            isLoadingCities={isLoadingCities}
            onCommunitySelect={handleCommunitySelect}
          />
        );
      case 2:
        return (
          <AdditionalInformationForm
            values={values}
            setFieldValue={setFieldValue}
            setFieldTouched={formik.setFieldTouched}
            errors={errors}
            touched={touched}
            categoryOptions={categoryOptions}
            businessHours={values.businessHours}
            toggleBusinessHour={toggleBusinessHour}
            businessHoursTimes={values.businessHoursTimes}
            setBusinessHoursTimes={(newTimes) => {
              const updatedValue =
                typeof newTimes === 'function' ? newTimes(values.businessHoursTimes) : newTimes;
              setFieldValue('businessHoursTimes', updatedValue);
            }}
            businessPhones={values.businessPhones}
            setBusinessPhones={(newPhones) => {
              const updatedValue =
                typeof newPhones === 'function' ? newPhones(values.businessPhones) : newPhones;
              setFieldValue('businessPhones', updatedValue);
            }}
            socialLinks={values.socialLinks}
            setSocialLinks={(newLinks) => {
              const updatedValue =
                typeof newLinks === 'function' ? newLinks(values.socialLinks) : newLinks;
              setFieldValue('socialLinks', updatedValue);
            }}
            externalLinkTypes={externalLinkTypes || []}
            isLoadingExternalLinks={isLoadingExternalLinks}
          />
        );
      case 3:
        return (
          <BusinessPhotosUpload
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            fileStatuses={fileStatuses}
            setFileStatuses={setFileStatuses}
            isDragOver={isDragOver}
            setIsDragOver={setIsDragOver}
            handleFileSelect={handleFileSelect}
            handleDragOver={handleDragOver}
            handleDragLeave={handleDragLeave}
            handleDrop={handleDrop}
            handleFileInputChange={handleFileInputChange}
            removeFile={removeFile}
            retryUpload={retryUpload}
            previewFile={previewFile}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-full bg-white px-4 md:px-16 pt-0 md:py-8 pb-6">
      {/* Mobile Header and Stepper Combined - Fixed below the main header */}
      <div className="md:hidden fixed top-[60px] left-0 right-0 z-30 bg-white px-4 pt-3 pb-3 border-b border-borderGrey">
        {/* Header with back button */}
        <div className="flex items-center gap-2 pb-3">
          <button
            onClick={() => {
              if (currentStep > 1) {
                handlePrevious();
              } else {
                navigate(-1);
              }
            }}
            className="flex items-center justify-center w-7 h-7"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 18L9 12L15 6"
                stroke="#111653"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h1 className="text-base font-semibold text-darkBlue font-poppins">
            Business Onboarding
          </h1>
        </div>

        {/* Stepper */}
        <ProgressIndicator currentStep={currentStep} totalSteps={totalSteps} />
      </div>

      {/* Desktop Stepper */}
      <div className="hidden md:block mb-6">
        <ProgressIndicator currentStep={currentStep} totalSteps={totalSteps} />
      </div>

      {/* Desktop Title */}
      <div className="hidden md:block mb-6">
        <div className="bg-[#F3F0FF] rounded-xl shadow-sm py-3 sm:py-4 px-4">
          <h1 className="text-base sm:text-lg font-medium text-[#6B46C1] leading-none font-poppins">
            Business Onboarding
          </h1>
        </div>
      </div>

      {/* Form Content - Add top padding on mobile to account for fixed header */}
      <div className="md:px-0 mb-6 mt-[120px] md:mt-0">{renderStepContent()}</div>

      {/* Navigation Buttons */}
      <div className="pb-20 md:pb-0">
        <NavigationButtons
          currentStep={currentStep}
          totalSteps={totalSteps}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onSkip={handleSkip}
          isSubmitting={isSubmitting || isSubmittingPhotos}
          isNextDisabled={currentStep === 1 && (!isStep1Complete || !formik.dirty)}
        />
      </div>
    </div>
  );
};

export default OnboardingBusiness;
